/**
 * src/domain/value-objects/localized-string.ts
 *
 * LocalizedString — Domain'de bir alanın locale → string eşlemesi.
 *
 * β.2.5 ile shape `Record<string, string>` olarak genişledi. Önceki
 * `Record<'tr' | 'en', string>` hardcoded literal'dı; yeni dil eklemek
 * Domain dosyasına dokunmayı gerektiriyordu. Artık runtime'da herhangi
 * bir locale code key olarak yazılabilir; geçerli key listesi caller'ın
 * sorumluluğunda (`getValidLocales()` registry-driven).
 *
 * Domain'de framework import YOK; runtime JSONB key set'i siteSettings.locales[]'den
 * gelir ama Domain bu kaynağa bağımlı değildir. `pickLocalized` son-çare
 * fallback olarak Domain'in `DOMAIN_FALLBACK_LOCALE` sigortasına düşer.
 *
 * Referans: ADR-0002, spec `2026-05-26-phase-beta-2-5-schema-rework-design.md` §3.
 */

import { DOMAIN_FALLBACK_LOCALE, type Locale } from './locale'

/**
 * Locale code → string map. Key'ler runtime'da serbest; her zaman bir registry'ye
 * (siteSettings.locales[].code) karşılık gelmesi beklenir ama Domain bunu
 * enforce etmez.
 *
 * `Readonly` — Domain VO immutability: caller'ların kazara `value.tr = '...'`
 * mutasyonu yapmasını compiler bloklar. Yeni içerik için `emptyLocalizedString`
 * builder kullan; mevcut value'yu güncellemek için spread + yeniden ata
 * (`setBaslik({ ...baslik, [locale]: newText })`).
 */
export type LocalizedString = Readonly<Record<string, string>>

/**
 * Belirli bir locale için string döner; locale'da değer yoksa fallback locale'a,
 * o da yoksa boş string'e düşer.
 *
 * Fallback parametresi opsiyoneldir — verilmezse Domain'in son-çare sigortası
 * `DOMAIN_FALLBACK_LOCALE` kullanılır. Caller authoritative default'u biliyorsa
 * (siteSettings.defaultLocale gibi) explicit geçirmelidir; ancak siteSettings
 * okunamadığı boot-time / failure path senaryolarında bile bu fonksiyon hâlâ
 * çalışır.
 *
 * @param value LocalizedString map (örn. `{ tr: 'Merhaba', en: 'Hello' }`)
 * @param locale Hedef locale code
 * @param fallback Fallback locale code (default: DOMAIN_FALLBACK_LOCALE)
 * @returns Hedef veya fallback locale'daki string; ikisi de yoksa empty string
 *
 * @example
 * ```ts
 * // Implicit fallback (Domain'in 'tr' sigortası)
 * pickLocalized(hizmet.baslik, currentLocale)
 *
 * // Explicit fallback (siteSettings authoritative default)
 * const defaultLocale = await getDefaultLocale()
 * pickLocalized(hizmet.baslik, currentLocale, defaultLocale)
 * ```
 */
export function pickLocalized(
  value: LocalizedString,
  locale: Locale,
  fallback: Locale = DOMAIN_FALLBACK_LOCALE,
): string {
  return value[locale] || value[fallback] || ''
}

/**
 * Verilen locale registry'sinden tüm key'ler için boş string içeren bir
 * LocalizedString üretir. Yeni içerik oluşturulurken form state'inin başlangıç
 * değeri olarak kullanılır.
 *
 * @param locales Locale code listesi (caller siteSettings.locales[].code'dan geçirir)
 * @returns Her locale için empty string içeren map (örn. `{ tr: '', en: '' }`)
 */
export function emptyLocalizedString(locales: readonly Locale[]): LocalizedString {
  return Object.fromEntries(locales.map((l) => [l, '']))
}

/**
 * pickLocalizedOrNull — `pickLocalized`'in nullable varyantı. Boş string yerine
 * `null` döner; SEO title/description, OG metadata, anchor label gibi
 * "boş string yerine 'render etme' demek" senaryolarda kullanılır.
 *
 * `pickLocalized` her zaman string döner (boş string fallback), o nedenle
 * `Boolean(...)` veya `?.length` kontrolüne ihtiyaç var. Bu varyant açıkça
 * `null | string` döner; type narrowing daha temiz.
 *
 * @example
 * ```ts
 * const title = pickLocalizedOrNull(hizmet.baslik, locale)
 * if (!title) return notFound()
 * return <title>{title} | SecureSys</title>
 * ```
 */
export function pickLocalizedOrNull(
  value: LocalizedString,
  locale: Locale,
  fallback: Locale = DOMAIN_FALLBACK_LOCALE,
): string | null {
  return value[locale] || value[fallback] || null
}
